// Enumerations
import { AppErrorEnumeration } from 'enumerations/app-error.enumeration';
import { AppStatesEnumeration } from 'enumerations/app-states.enumeration';
import { KycMethodsEnumeration } from 'containers/kyc/enumerations/kyc-methods.enumeration';
import { KycStatesEnumeration } from 'containers/kyc/enumerations/kyc-states.enumeration';
import { WebSocketPayloadStatusEnumeration } from 'enumerations/wbsocket-payload-status.enumeration';
// Resolvers
import {
  completeAccountCheckResolver,
  invalidateAccountCheckResolver,
} from '../resolvers/kyc-method.resolver';
// Types
import {
  KycMethodsAccountCheckEventsListenerType,
  KycMethodsBuilderHandlersType,
  KycMethodsCompleteBuilderHandlersType,
  KycMethodsIframeBuilderHandlersType,
  KycMethodsInvalidateBuilderHandlersType,
  KycMethodsMethodBuilderHandlersType,
  KycMethodsMethodsHandlersType,
  KycMethodsVideocallEventsListenerType,
} from './types/kyc-method.handlers.type';
import { WsResponseType } from 'types/ws-response.type';
// Utils
import { eventIsNotFromTink, isAccountReportType, isErrorType } from 'utils/identification.utils';
import {
  eventIsNotFromVeridas,
  isQrProcessFinished,
  shouldShowErrorFromEvent,
} from 'containers/upload-id/views/verification/handlers/utils/upload-id-verification.handlers.utils';

const manageReceivedDataFromKycWSEventHandler = ({
  message,
  setCurrentState,
  setError,
}: KycMethodsMethodBuilderHandlersType): void => {
  const state = message?.detail?.payload?.status as unknown as WebSocketPayloadStatusEnumeration;

  switch (state) {
    case WebSocketPayloadStatusEnumeration.ACCEPTED:
      setCurrentState(AppStatesEnumeration.LOADING);
      break;
    case WebSocketPayloadStatusEnumeration.REJECTED:
    default:
      setError(AppErrorEnumeration.KYC_METHOD_WS_STATUS_REJECTED);
  }
};

const completeAccountCheckHandler = async ({
  kycIdentificationId,
  setCurrentState,
  setError,
  reportIds,
}: KycMethodsCompleteBuilderHandlersType): Promise<void> => {
  const [error] = await completeAccountCheckResolver({ kycIdentificationId, reportIds });
  if (error) {
    setError(AppErrorEnumeration.COMPLETE_ACCOUNT_CHECK_FAILED);
  } else {
    setCurrentState(AppStatesEnumeration.LOADING);
  }
};

const invalidateAccountCheckHandler = async ({
  kycIdentificationId,
  setError,
}: KycMethodsInvalidateBuilderHandlersType): Promise<void> => {
  await invalidateAccountCheckResolver(kycIdentificationId);
  setError(AppErrorEnumeration.ACCOUNT_CHECK_INVALID_TINK_ERROR);
};

const manageReceiveDataFromKycAccountCheckIframeEventHandler = async ({
  event,
  kycIdentificationId,
  setError,
  setCurrentState,
}: KycMethodsAccountCheckEventsListenerType): Promise<void> => {
  if (eventIsNotFromTink(event.origin)) {
    return;
  }
  const { type, data, error } = JSON.parse(event.data);
  if (isAccountReportType(type)) {
    await completeAccountCheckHandler({
      kycIdentificationId,
      setCurrentState,
      setError,
      reportIds: { account_verification_report_id: data },
    });
  } else if (isErrorType(type) || error) {
    await invalidateAccountCheckHandler({ kycIdentificationId, setError });
  }
};

const manageReceiveDataFromKycVeridasIframeEventHandler = ({
  event,
  setError,
  setShowLoader,
}: KycMethodsVideocallEventsListenerType): void => {
  if (eventIsNotFromVeridas(event.origin)) {
    return;
  }
  const { type, code, infoEvent } = event.data;

  if (shouldShowErrorFromEvent({ code, type, infoEvent })) {
    setError(`${AppErrorEnumeration.VERIDAS_VIDEOCALL_ERROR_EVENT}_${code}` as AppErrorEnumeration);
  }
  if (isQrProcessFinished(infoEvent?.code)) {
    setShowLoader(true);
  }
};

const manageKycEventsListenerHandler = ({
  event,
  selectedMethod,
  kycIdentificationId,
  setError,
  setCurrentState,
  setShowLoader,
}: KycMethodsIframeBuilderHandlersType): void =>
  ({
    [KycMethodsEnumeration.ACCOUNT_CHECK]: () =>
      manageReceiveDataFromKycAccountCheckIframeEventHandler({
        event,
        kycIdentificationId,
        setError,
        setCurrentState,
      }),
    [KycMethodsEnumeration.VIDEOCALL]: () =>
      manageReceiveDataFromKycVeridasIframeEventHandler({ event, setError, setShowLoader }),
  }[selectedMethod as KycMethodsEnumeration]());

const KycMethodHandlers = (
  props: KycMethodsBuilderHandlersType
): KycMethodsMethodsHandlersType => ({
  handleManageReceivedDataFromKycWSEvent: (message: WsResponseType<KycStatesEnumeration>) =>
    manageReceivedDataFromKycWSEventHandler({ ...props, message }),
  handleManageReceivedDataFromKycIframeEvent: (event: MessageEvent) =>
    manageKycEventsListenerHandler({ ...props, event }),
});

export { KycMethodHandlers };
